import type { CSSProperties } from 'react';
import { cx } from '@/lib/cx';
import { initials } from '@/lib/format';

/**
 * Muted tints that sit with the ink chrome: [background, monogram]. Avatars and satellite
 * imagery are where colour lives; keep these quiet so neither shouts.
 */
const TINTS: ReadonlyArray<readonly [string, string]> = [
  ['#dde7da', '#2f4d36'], // sage
  ['#f1ddd2', '#7a3d22'], // clay
  ['#dae2ee', '#2d4462'], // slate
  ['#efe5cc', '#67511c'], // sand
  ['#f0dae1', '#7a2e46'], // rose
  ['#e1e6cb', '#4c571c'], // moss
  ['#e3dfee', '#463c68'], // heather
  ['#d6e6e5', '#25524f'], // lagoon
];

/** FNV-1a: stable across sessions and browsers, so a company keeps its tint everywhere. */
function hash(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Tint for a person: by company when known (colleagues share a colour), else by name. */
export function avatarTint(name: string | null | undefined, company?: string | null): readonly [string, string] {
  const key = (company?.trim() || name?.trim() || '').toLowerCase();
  return TINTS[hash(key) % TINTS.length]!;
}

export interface AvatarProps {
  name: string | null | undefined;
  /** Company name; people of one company share a tint. */
  company?: string | null;
  /** Diameter in px. Default 32. */
  size?: number;
  className?: string;
  /** Decorative by default (the name is usually printed next to it); pass true to announce the name. */
  labelled?: boolean;
}

/** Person monogram on a deterministic tint. */
export function Avatar({ name, company, size = 32, className, labelled = false }: AvatarProps) {
  const [bg, fg] = avatarTint(name, company);
  const style = { '--avatar-size': `${size}px`, '--avatar-bg': bg, '--avatar-fg': fg } as CSSProperties;
  return (
    <span
      className={cx('avatar', className)}
      style={style}
      role={labelled ? 'img' : undefined}
      aria-label={labelled ? (name ?? 'Unknown person') : undefined}
      aria-hidden={labelled ? undefined : true}
      title={labelled ? undefined : (name ?? undefined)}
    >
      {initials(name)}
    </span>
  );
}
